import ScriptLoader, { ScriptStatus } from './ScriptLoader';
import { getContainer } from '../ioc/IOCSetup';
import { UserInfo } from '../http/protocol';
import { useSelector } from 'react-redux';
import { AppState } from '../RootReducer';
import { LoginStatus } from '../http/Types';

// DO NOT CHANGE THESE AS THEY MUST MATCH THE EVENTS IN THE GLIFE MINI PROGRAM
export enum GLIFE_EVENTS {
  ESTABLISH_COMMUNICATION = "ESTABLISH_COMMUNICATION",
  SEND_AUTH_CODE = "SEND_AUTH_CODE",
  START_DEPOSIT = "START_DEPOSIT",
  OPEN_EXTERNAL_LINK = "OPEN_EXTERNAL_LINK"
};
export enum GLIFE_RESULTS {
  NOT_INITIALIZED = "NOT_INITIALIZED",
  OK = "OK",
  ERROR = "ERROR"
}

export const USER_DID_NOT_CONSENT_DATA_SHARING_CODE = "4005";
export const GLIFE_SCRIPT_ID = "glife-script";

class GlifeScriptSingleton {
  private static instance: GlifeScriptSingleton;
  private initialized = false;
  private results: { [key in GLIFE_EVENTS]?: GLIFE_RESULTS } = {
    [GLIFE_EVENTS.START_DEPOSIT]: GLIFE_RESULTS.NOT_INITIALIZED,
    [GLIFE_EVENTS.OPEN_EXTERNAL_LINK]: GLIFE_RESULTS.NOT_INITIALIZED
  };
  private successCallbackSendAuthCode?: (userInfo: UserInfo) => void;
  private errorCallbackSendAuthCode?: (error: Error) => void;
  private postAuthCode: any;

  private constructor() { }

  static getInstance(): GlifeScriptSingleton {
    if (!GlifeScriptSingleton.instance) {
      GlifeScriptSingleton.instance = new GlifeScriptSingleton();
    }
    return GlifeScriptSingleton.instance;
  }

  initialize(handleDepositResult: (payload: any) => void, handleOpenExternalLinkResult: (payload: any) => void, successCallbackSendAuthCode?: (userInfo: UserInfo) => void, errorCallbackSendAuthCode?: (error: Error) => void, postAuthCode?: any, overrideAuthCode?: string | null) {
    if (this.initialized) return;

    if (successCallbackSendAuthCode) {
      this.successCallbackSendAuthCode = successCallbackSendAuthCode;
    }

    if (errorCallbackSendAuthCode) {
      this.errorCallbackSendAuthCode = errorCallbackSendAuthCode;
    }

    if (postAuthCode) {
      this.postAuthCode = postAuthCode;
    }

    if (overrideAuthCode) {
      this.handleSendAuthCode({ authCode: overrideAuthCode });
    }

    const handleMessage = (e: { event: GLIFE_EVENTS, payload: {} }) => {
      const { event, payload } = e;

      switch (event) {
        case GLIFE_EVENTS.SEND_AUTH_CODE:
          return this.handleSendAuthCode(payload);
        case GLIFE_EVENTS.START_DEPOSIT:
          return handleDepositResult(payload);
        case GLIFE_EVENTS.OPEN_EXTERNAL_LINK:
          return handleOpenExternalLinkResult(payload);
        case GLIFE_EVENTS.ESTABLISH_COMMUNICATION:
        default:
      }
    };

    ScriptLoader.loadScript("https://appx/web-view.min.js", GLIFE_SCRIPT_ID);

    ScriptLoader.subscribe(GLIFE_SCRIPT_ID, (status) => {
      if (status === ScriptStatus.DONE) {
        const my = (window as any).my;
        if (!my) {
          return;
        }
        my.onMessage = handleMessage;
        this.postMessage(GLIFE_EVENTS.ESTABLISH_COMMUNICATION, {});
      }
    });

    sessionStorage.setItem(GLIFE_SCRIPT_ID, "true");

    this.initialized = true;
  }

  async handleSendAuthCode(payload: any) {
    try {
      // User has not consented to Gcash data sharing with mini program
      if (payload?.authErrorScope?.auth_user === USER_DID_NOT_CONSENT_DATA_SHARING_CODE) {
        throw new Error(USER_DID_NOT_CONSENT_DATA_SHARING_CODE);
      }

      let deviceId: string = "";
      try {
        deviceId = (await window.fingerprint.get()).visitorId;
      } catch (e) {
        console.log("Failed to get fingerprint id");
      }

      const response: UserInfo = await this.postAuthCode(payload.authCode, deviceId) as UserInfo;
      this.successCallbackSendAuthCode && this.successCallbackSendAuthCode(response);
    } catch (error) {
      this.errorCallbackSendAuthCode && this.errorCallbackSendAuthCode(error);
    }
  };

  postMessage(event: GLIFE_EVENTS, payload: {}) {
    const my = (window as any).my;
    if (!my) {
      return;
    }

    my.postMessage({ event, payload });
  }

  getStatus(): ScriptStatus {
    return ScriptLoader.getStatus(GLIFE_SCRIPT_ID);
  }

  getResult(event: GLIFE_EVENTS): GLIFE_RESULTS {
    return this.results[event] as GLIFE_RESULTS;
  }

  setResult(event: GLIFE_EVENTS, result: GLIFE_RESULTS) {
    this.results[event] = result;
  }

  getHasBeenInitialized() {
    return this.initialized;
  }
}

const useGlifeCommunication = (successCallbackSendAuthCode?: (userInfo: UserInfo) => void, errorCallbackSendAuthCode?: (error: Error) => void, overrideAuthCode?: string | null) => {
  const isLoggedIn = useSelector((state: AppState) => state.user.status === LoginStatus.LOGGED_IN);

  const postAuthCode = async (authCode: string, deviceId: string) => await getContainer().getUserService().sendGlifeAuthCode(authCode, deviceId);
  const glifeScript = GlifeScriptSingleton.getInstance();

  const handleDepositResult = (payload: { result: GLIFE_RESULTS }) => {
    glifeScript.setResult(GLIFE_EVENTS.START_DEPOSIT, payload.result);
  };

  const handleOpenExternalLinkResult = (payload: { result: GLIFE_RESULTS }) => {
    glifeScript.setResult(GLIFE_EVENTS.OPEN_EXTERNAL_LINK, payload.result);
  };

  const handleStartDeposit = (paymentUrl: string) => {
    glifeScript.postMessage(GLIFE_EVENTS.START_DEPOSIT, { paymentUrl });
  };

  const handleOpenExternalLink = (linkURL: string) => {
    glifeScript.postMessage(GLIFE_EVENTS.OPEN_EXTERNAL_LINK, { linkURL });
  };

  // In normal flow we have a race condition where the first time this hook is called and the user is NOT logged in it will not have the callbacks set, because another component will initialize the hook without the callbacks set. Thus we cannot initialize the script until the hook is initialized with the callbacks.
  // When a user reloads the app and is already logged in, the script can be initialized without the callbacks, thus we have to check if the user is already logged in and initialize the script without the callbacks.

  // If a user is already logged in we will never use the auth code callbacks, thus we should initialize the script without the callbacks
  if(isLoggedIn && !glifeScript.getHasBeenInitialized()) {
    glifeScript.initialize(handleDepositResult, handleOpenExternalLinkResult);
  }

  // Else we MUST have callbacks to be able to log in the user with the auth code, thus we have to initialize the script with the callbacks
  if (!isLoggedIn &&!glifeScript.getHasBeenInitialized() && successCallbackSendAuthCode && errorCallbackSendAuthCode && postAuthCode) {
    glifeScript.initialize(handleDepositResult, handleOpenExternalLinkResult, successCallbackSendAuthCode, errorCallbackSendAuthCode, postAuthCode, overrideAuthCode);
  }

  return { handleStartDeposit, handleOpenExternalLink, getResult: glifeScript.getResult.bind(glifeScript) };
};

export default useGlifeCommunication;
